'use client';

import { useRef, useEffect } from 'react';
import Image from 'next/image';
import gsap from 'gsap';
import { cn } from '../../lib/utils.js';
import { Container } from '../layout/Container.jsx';
import { Button } from '../ui/Button.jsx';

/**
 * Hero Section component with GSAP animations
 */
export function HeroSection({
  headline,
  subheadline,
  description,
  cta,
  logo,
  backgroundImage,
  backgroundImageAlt = '',
  backgroundGradient,
  backgroundVideo,
  overlay = true,
  overlayOpacity = 0.5,
  fullHeight = true,
  alignment = 'center',
  className,
}) {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!contentRef.current) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.3 });

      // Animate content elements
      tl.fromTo(
        contentRef.current.querySelectorAll('.hero-animate'),
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const alignments = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end',
  };

  return (
    <section
      ref={sectionRef}
      className={cn(
        'relative overflow-hidden',
        fullHeight ? 'min-h-screen' : 'min-h-[70vh]',
        'flex items-center',
        className
      )}
    >
      {/* Abstract Background Pattern */}
      <div className="absolute inset-0 -z-10">
        <div className={cn(
          "absolute inset-0",
          backgroundGradient
            ? `bg-gradient-to-br ${backgroundGradient}`
            : "bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-black"
        )} />
        <svg className="absolute inset-0 w-full h-full opacity-30 dark:opacity-20" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className={backgroundGradient ? "text-white/20" : "text-gray-300 dark:text-gray-700"}/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      {/* Background Image */}
      {backgroundImage && !backgroundVideo && (
        <div className="absolute inset-0 -z-10">
          <Image
            src={backgroundImage}
            alt={backgroundImageAlt}
            fill
            className="object-cover"
            priority
            quality={90}
            sizes="100vw"
          />
        </div>
      )}

      {/* Background Video */}
      {backgroundVideo && (
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={backgroundVideo} type="video/mp4" />
          </video>
        </div>
      )}

      {/* Overlay - only for images/videos, not gradients */}
      {overlay && (backgroundImage || backgroundVideo) && !backgroundGradient && (
        <div
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
        />
      )}

      {/* Content */}
      <Container className="relative z-10 mb-4">
        <div
          ref={contentRef}
          className={cn(
            'flex flex-col max-w-3xl',
            alignments[alignment],
            alignment === 'center' && 'mx-auto'
          )}
        >
          {logo && (
            <div className="hero-animate mb-8 mt-16 md:mt-0">
              {logo.light && logo.dark ? (
                <>
                  <Image
                    src={logo.light}
                    alt={logo.alt || 'Logo'}
                    width={800}
                    height={240}
                    className="dark:hidden w-40 h-auto md:w-[250px]"
                    priority
                    quality={100}
                  />
                  <Image
                    src={logo.dark}
                    alt={logo.alt || 'Logo'}
                    width={800}
                    height={240}
                    className="hidden dark:block w-40 h-auto md:w-[250px]"
                    priority
                    quality={100}
                  />
                </>
              ) : (
                <Image
                  src={logo.light || logo.dark || logo}
                  alt={logo.alt || 'Logo'}
                  width={800}
                  height={240}
                  className="w-40 h-auto md:w-[250px]"
                  priority
                  quality={100}
                />
              )}
            </div>
          )}

          {subheadline && (
            <span className={cn(
              "hero-animate text-sm uppercase tracking-widest mb-4",
              (backgroundImage || backgroundVideo || backgroundGradient) ? 'text-white/80' : 'text-accent-dark dark:text-accent'
            )}>
              {subheadline}
            </span>
          )}

          {headline && (
            <h1
              className={cn(
                'hero-animate font-heading font-bold',
                'text-4xl md:text-5xl lg:text-5xl xl:text-5xl white-space: break-spaces',
                'mb-6',
                (backgroundImage || backgroundVideo || backgroundGradient) ? 'text-white dark:text-white' : 'text-primary dark:text-white'
              )}
            >
              {headline}
            </h1>
          )}

          {description && (
            <p
              className={cn(
                'hero-animate text-lg md:text-xl mb-8',
                (backgroundImage || backgroundVideo || backgroundGradient) ? 'text-white/90' : 'text-gray-600 dark:text-gray-300'
              )}
            >
              {description}
            </p>
          )}

          {cta && (
            <div className="hero-animate flex flex-col md:flex-row flex-wrap gap-3 md:gap-4 w-full md:w-auto">
              {cta.primary && (
                <Button
                  href={cta.primary.href}
                  variant={(backgroundImage || backgroundVideo || backgroundGradient) ? 'ghost' : 'primary'}
                  size="md"
                  className={cn(
                    'w-full md:w-auto md:px-8 md:py-4 md:text-lg',
                    (backgroundImage || backgroundVideo || backgroundGradient) ? 'text-white border-white hover:bg-white hover:text-primary dark:text-gray-50' : ''
                  )}
                >
                  {cta.primary.label}
                </Button>
              )}
              {cta.secondary && (
                <Button
                  href={cta.secondary.href}
                  variant={(backgroundImage || backgroundVideo || backgroundGradient) ? 'ghost' : 'secondary'}
                  size="md"
                  className={cn(
                    'w-full md:w-auto md:px-8 md:py-4 md:text-lg',
                    (backgroundImage || backgroundVideo || backgroundGradient) ? 'text-white border-white hover:bg-white hover:text-primary' : ''
                  )}
                >
                  {cta.secondary.label}
                </Button>
              )}
            </div>
          )}
        </div>
      </Container>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className={cn(
          'w-6 h-10 rounded-full border-2 flex justify-center pt-2',
          (backgroundImage || backgroundVideo || backgroundGradient) ? 'border-white' : 'border-primary dark:border-white'
        )}>
          <div className={cn(
            'w-1 h-2 rounded-full',
            (backgroundImage || backgroundVideo || backgroundGradient) ? 'bg-white' : 'bg-primary dark:bg-white'
          )} />
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
